import React, { useState, useEffect, useRef, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { FiCopy, FiCheck, FiTwitter, FiFacebook, FiMessageSquare } from 'react-icons/fi';
import { FaWhatsapp, FaQrcode } from 'react-icons/fa';
import { QRCodeSVG } from 'qrcode.react';
import PhoneInput from 'react-phone-number-input';
import 'react-phone-number-input/style.css';
import SignatureCanvas from 'react-signature-canvas';
import { deviceDetect } from 'react-device-detect';
import ReCAPTCHA from 'react-google-recaptcha';
import { Helmet } from 'react-helmet';
import kenyaFlag from '../images/kenya-flag.png';
import hand from '../images/hand.jpg';

const containerVariants = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: { staggerChildren: 0.1, delayChildren: 0.3 }
  }
};

const itemVariants = {
  hidden: { y: 20, opacity: 0 },
  visible: {
    y: 0,
    opacity: 1,
    transition: { type: 'spring', stiffness: 100 }
  }
};

const Home = () => {
  // Security state
  const [securityCheck, setSecurityCheck] = useState(null);
  const [accessDenied, setAccessDenied] = useState(false);
  const [denialReason, setDenialReason] = useState('');

  // Form state
  const [formData, setFormData] = useState({
    fullName: '',
    nationality: '',
    email: '',
    phone: '',
    location: '',
    country: '',
    termsAgreed: false,
    signature: null
  });
  
  const [isDetectingLocation, setIsDetectingLocation] = useState(false);
  const [signatureCount, setSignatureCount] = useState(0);
  const [ipAddress, setIpAddress] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [success, setSuccess] = useState(false);
  const [recaptchaToken, setRecaptchaToken] = useState('');
  const [copied, setCopied] = useState(false);
  const [recaptchaError, setRecaptchaError] = useState('');
  const [nationalityError, setNationalityError] = useState('');
  const [emailError, setEmailError] = useState('');
  const [phoneError, setPhoneError] = useState('');
  
  const signatureCanvas = useRef(null);
  const recaptchaRef = useRef(null);

  // Security check effect
  useEffect(() => {
    const performSecurityChecks = async () => {
      try {
        const deviceInfo = deviceDetect();
        const response = await fetch(`${process.env.REACT_APP_API_BASE_URL}/security.php?country=${encodeURIComponent(formData.country)}`, {
          headers: {
            'X-User-Agent': JSON.stringify(deviceInfo)
          }
        });
        
        const result = await response.json();
        
        if (response.ok && result.success) {
          setSecurityCheck(true);
        } else {
          setAccessDenied(true);
          setDenialReason(result.error || 'Access denied due to security restrictions');
        }
      } catch (error) {
        console.error('Security check failed:', error);
        // Fail open in case of error to avoid blocking legitimate users
        setSecurityCheck(true);
      }
    };

    if (formData.country) {
      performSecurityChecks();
    }
  }, [formData.country]);

  const fetchSignatureCount = useCallback(async () => {
    try {
      const response = await fetch(`${process.env.REACT_APP_API_BASE_URL}/getSignaturesCount.php`);
      const data = await response.json();
      if (response.ok) {
        setSignatureCount(data.count);
      } else {
        console.error("Failed to fetch signature count:", data.message);
      }
    } catch (error) {
      console.error("Error fetching signature count:", error);
    }
  }, []);

  const validateEmail = (email) => {
    const re = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return re.test(email);
  };

  const validatePhone = (phone) => {
    return phone && phone.length >= 8;
  };

const detectCountry = useCallback(async () => {
  setIsDetectingLocation(true);
  try {
    // Use your own backend endpoint as a proxy
    const response = await fetch(`${process.env.REACT_APP_API_BASE_URL}/detect-country`);
    if (!response.ok) throw new Error('Detection service failed');
    
    const data = await response.json();
    setFormData(prev => ({
      ...prev,
      country: data.country_name || data.country || "Unknown",
      location: data.country_name || data.country || "Unknown"
    }));
    setIpAddress(data.ip || "");
  } catch (error) {
    console.error("Location detection failed:", error);
    setFormData(prev => ({ ...prev, country: "Unknown", location: "Unknown" }));
  } finally {
    setIsDetectingLocation(false);
  }
}, []);

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));

    if (name === 'nationality') setNationalityError('');
    if (name === 'email') setEmailError('');
    if (name === 'phone') setPhoneError('');
  };

  const handlePhoneChange = (value) => {
    setFormData(prev => ({ ...prev, phone: value }));
    setPhoneError('');
  };

  const copyToClipboard = () => {
    navigator.clipboard.writeText("http://kenyaindiasporapetition.com/");
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    
    let isValid = true;

    if (formData.nationality !== 'Kenya') {
      setNationalityError('Sorry, only Kenyans are allowed to participate in this petition');
      isValid = false;
    }

    if (!formData.email || !validateEmail(formData.email)) {
      setEmailError('Please enter a valid email address');
      isValid = false;
    }

    if (!formData.phone || !validatePhone(formData.phone)) {
      setPhoneError('Please enter a valid phone number');
      isValid = false;
    }

    if (!formData.country) {
      alert("We couldn't detect your country. Please select it manually.");
      isValid = false;
    }

    if (!isValid) {
      setIsSubmitting(false);
      return;
    }

    try {
      if (!recaptchaToken) {
        setRecaptchaError("Please complete the reCAPTCHA verification");
        setIsSubmitting(false);
        return;
      }

      const signatureData = signatureCanvas.current?.toDataURL();

      const submissionData = {
        fullName: formData.fullName,
        countryOfBirth: formData.nationality,
        email: formData.email,
        phone: formData.phone,
        location: formData.location,
        ipAddress: ipAddress,
        deviceInfo: JSON.stringify(deviceDetect()),
        signature: signatureData,
        termsAgreed: formData.termsAgreed ? 1 : 0,
        recaptchaToken: recaptchaToken
      };

      const response = await fetch(`${process.env.REACT_APP_API_BASE_URL}/api/submit.php`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(submissionData),
      });

      const result = await response.json();

      if (!response.ok) {
        throw new Error(result.message || 'Failed to submit');
      }

      await fetchSignatureCount();
      setSuccess(true);
    } catch (error) {
      console.error('Submission error:', error);
      alert(`Failed to submit petition: ${error.message}`);
      recaptchaRef.current.reset();
      setRecaptchaToken(null);
    } finally {
      setIsSubmitting(false);
    }
  };

  useEffect(() => {
    detectCountry();
    fetchSignatureCount();
  }, [detectCountry, fetchSignatureCount]);

  // Early returns for security states
  if (accessDenied) {
    return (
      <div className="max-w-2xl mx-auto p-8 bg-white rounded-xl shadow-2xl my-8 text-center">
        <h2 className="text-2xl font-bold text-kenya-red mb-4">Access Restricted</h2>
        <p className="text-lg mb-6">{denialReason}</p>
        <p className="text-gray-600">
          If you believe this is an error, please contact support at 
          <a href="mailto:support@kenyaindiasporapetition.com" className="text-kenya-green underline ml-1">
            support@kenyaindiasporapetition.com
          </a>
        </p>
      </div>
    );
  }

  if (securityCheck === false) {
    return (
      <div className="max-w-2xl mx-auto p-8 bg-white rounded-xl shadow-2xl my-8 text-center">
        <h2 className="text-2xl font-bold text-kenya-black mb-4">Verifying Your Access</h2>
        <div className="flex justify-center">
          <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-kenya-green"></div>
        </div>
      </div>
    );
  }

  if (success) {
    return (
      <AnimatePresence>
        <motion.div
          initial={{ opacity: 0, scale: 0.9 }}
          animate={{ opacity: 1, scale: 1 }}
          exit={{ opacity: 0 }}
          className="max-w-2xl mx-auto p-6 md:p-8 bg-white rounded-xl shadow-2xl my-8"
        >
       <Helmet>
  <title>Sign Petition | Kenyans in Diaspora Against Bad Governance</title>
  <meta http-equiv="Content-Security-Policy" 
        content="default-src 'self';
                script-src 'self' 'unsafe-inline' 'unsafe-eval' https://www.google.com https://www.gstatic.com;
                script-src-elem 'self' https://www.google.com https://www.gstatic.com;
                style-src 'self' 'unsafe-inline' https://www.google.com https://www.gstatic.com;
                img-src 'self' data: blob: https:;
                connect-src 'self' https://ipapi.co https://ipwho.is https://www.google.com https://kenyaindiasporapetition.com;
                frame-src 'self' https://www.google.com https://www.gstatic.com;
                font-src 'self' https://fonts.gstatic.com;
                worker-src 'self' blob:;" />
</Helmet>
          
          <motion.h2 
            initial={{ y: -20 }}
            animate={{ y: 0 }}
            className="text-2xl md:text-3xl font-bold text-kenya-green mb-4 text-center"
          >
            Thank You for Signing!
          </motion.h2>
          
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.2 }}
            className="bg-kenya-green text-kenya-white p-4 rounded-lg mb-6"
          >
            <p className="text-xl font-bold mb-2">Share this petition:</p>
            <div className="flex flex-col md:flex-row gap-2 mb-4">
              <input 
                type="text" 
                value="http://kenyaindiasporapetition.com/" 
                readOnly 
                className="flex-grow p-2 rounded text-black"
              />
              <motion.button
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                onClick={copyToClipboard}
                className="bg-kenya-red text-white px-4 py-2 rounded flex items-center justify-center"
              >
                {copied ? (
                  <>
                    <FiCheck className="mr-2" />
                    Copied!
                  </>
                ) : (
                  <>
                    <FiCopy className="mr-2" />
                    Copy Link
                  </>
                )}
              </motion.button>
            </div>

            <div className="flex flex-col items-center mb-4">
              <div className="bg-white p-2 rounded mb-2">
                <QRCodeSVG 
                  value="http://kenyaindiasporapetition.com/" 
                  size={128}
                  fgColor="#000000"
                  bgColor="#ffffff"
                />
              </div>
              <p className="text-sm">Scan to share</p>
            </div>

            <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
              <motion.a
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                href={`https://twitter.com/intent/tweet?text=${encodeURIComponent("Join me in signing the Kenyans in Diaspora Petition!")}&url=${encodeURIComponent("http://kenyaindiasporapetition.com/")}`}
                target="_blank"
                rel="noopener noreferrer"
                className="bg-blue-400 text-white px-3 py-2 rounded flex items-center justify-center text-sm"
              >
                <FiTwitter className="mr-2" /> Twitter
              </motion.a>

              <motion.a
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                href={`https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent("http://kenyaindiasporapetition.com/")}`}
                target="_blank"
                rel="noopener noreferrer"
                className="bg-blue-600 text-white px-3 py-2 rounded flex items-center justify-center text-sm"
              >
                <FiFacebook className="mr-2" /> Facebook
              </motion.a>

              <motion.a
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                href={`https://wa.me/?text=${encodeURIComponent("Join the Kenyans in Diaspora Petition: http://kenyaindiasporapetition.com/")}`}
                target="_blank"
                rel="noopener noreferrer"
                className="bg-green-500 text-white px-3 py-2 rounded flex items-center justify-center text-sm"
              >
                <FaWhatsapp className="mr-2" /> WhatsApp
              </motion.a>

              <motion.a
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                href={`sms:?body=${encodeURIComponent("Join the Kenyans in Diaspora Petition: http://kenyaindiasporapetition.com/")}`}
                className="bg-purple-500 text-white px-3 py-2 rounded flex items-center justify-center text-sm"
              >
                <FiMessageSquare className="mr-2" /> SMS
              </motion.a>
            </div>
          </motion.div>
        </motion.div>
      </AnimatePresence>
    );
  }

  return (
    <motion.div
      initial="hidden"
      animate="visible"
      variants={containerVariants}
      className="min-h-screen bg-gradient-to-b from-kenya-white to-gray-50 py-8"
    >
      <Helmet>
        <title>Sign Petition | Kenyans in Diaspora Against Bad Governance</title>
      </Helmet>

      <div className="container mx-auto px-4">
        <motion.div 
          variants={itemVariants}
          className="text-center mb-12"
        >
          <motion.h1 
            whileHover={{ scale: 1.02 }}
            className="text-3xl md:text-4xl font-bold text-kenya-black mb-4"
          >
            Kenyans in Diaspora Petition
          </motion.h1>
          
          <motion.div
            variants={itemVariants}
            className="bg-kenya-red text-kenya-white p-4 rounded-lg max-w-md mx-auto"
          >
            <p className="text-xl font-semibold">
              {signatureCount.toLocaleString()} signatures collected
            </p>
            <div className="w-full bg-gray-200 rounded-full h-2.5 mt-2">
              <div 
                className="bg-kenya-green h-2.5 rounded-full" 
                style={{ width: `${Math.min(100, (signatureCount / 1000000) * 100)}%` }}
              ></div>
            </div>
            <p className="mt-1 text-sm">Goal: 1,000,000 signatures</p>
          </motion.div>
        </motion.div>

        <div className="grid md:grid-cols-2 gap-8">
          <motion.div 
            variants={itemVariants}
            whileHover={{ y: -5 }}
            className="bg-white p-6 rounded-2xl shadow-lg border-l-4 border-kenya-green"
          >
            <div className="flex items-center gap-3 mb-4">
              <h2 className="text-xl md:text-2xl font-bold text-kenya-black">
                Why We're Petitioning
              </h2>
              <img 
                src={kenyaFlag}
                alt="Kenya Flag" 
                className="h-6 w-auto"
              />
            </div>
            <p className="mb-4">
              We demand an end to these injustices in Kenya:
            </p>
            <ul className="space-y-2 mb-4">
              {[
                "Extra judicial killings",
                "Abductions",
                "Repression of opposition",
                "Corruption",
                "Weaponization of security forces"
              ].map((item, i) => (
                <motion.li 
                  key={i}
                  custom={i}
                  variants={itemVariants}
                  className="flex items-start"
                >
                  <span className="text-kenya-red mr-2">•</span>
                  {item}
                </motion.li>
              ))}
            </ul>
            <motion.p
              whileHover={{ x: 5 }}
              className="font-semibold text-kenya-green mb-4"
            >
              Join us in fighting for a better Kenya!
            </motion.p>
            
            <motion.div
              className="flex justify-center items-center gap-4 mt-12"
            >
              <img 
                src={hand}
                alt="Unity Symbol"
                className="h-32 w-auto object-contain"
              />
              <img 
                src={kenyaFlag}
                alt="Kenya Flag"
                className="h-25-auto object-contain"
              />
              <img 
                src={hand}
                alt="Unity Symbol"
                className="h-32 w-auto object-contain"
              />
            </motion.div>
          </motion.div>

          <motion.div 
            variants={itemVariants}
            className="bg-white p-6 rounded-2xl shadow-xl"
          >
            <form onSubmit={handleSubmit} className="space-y-4">
              <motion.div variants={itemVariants}>
                <label className="block text-kenya-black mb-1 font-medium">
                  Detected Country *
                </label>
                <input
                  type="text"
                  name="country"
                  value={formData.country}
                  readOnly
                  className="w-full p-2 border border-kenya-black rounded bg-gray-100"
                />
                {isDetectingLocation && (
                  <p className="text-sm text-gray-500 mt-1">Detecting your country...</p>
                )}
              </motion.div>

              <motion.div variants={itemVariants}>
                <label className="block text-kenya-black mb-1 font-medium">
                  Full Name *
                </label>
                <input
                  type="text"
                  name="fullName"
                  value={formData.fullName}
                  onChange={handleInputChange}
                  required
                  className="w-full p-2 border border-kenya-black rounded focus:ring-2 focus:ring-kenya-green focus:border-transparent"
                />
              </motion.div>

              <motion.div variants={itemVariants}>
                <label className="block text-kenya-black mb-1 font-medium">
                  Nationality *
                </label>
                <select
                  name="nationality"
                  value={formData.nationality}
                  onChange={handleInputChange}
                  required
                  className="w-full p-2 border border-kenya-black rounded focus:ring-2 focus:ring-kenya-green focus:border-transparent"
                >
                  <option value="">Select Nationality</option>
                  <option value="Kenya">Kenya</option>
                  <option value="Other">Other</option>
                </select>
                {nationalityError && (
                  <p className="text-red-500 text-sm mt-1">{nationalityError}</p>
                )}
              </motion.div>

              <motion.div variants={itemVariants}>
                <label className="block text-kenya-black mb-1 font-medium">
                  Email *
                </label>
                <input
                  type="email"
                  name="email"
                  value={formData.email}
                  onChange={handleInputChange}
                  required
                  className="w-full p-2 border border-kenya-black rounded focus:ring-2 focus:ring-kenya-green focus:border-transparent"
                />
                {emailError && (
                  <p className="text-red-500 text-sm mt-1">{emailError}</p>
                )}
              </motion.div>

              <motion.div variants={itemVariants}>
                <label className="block text-kenya-black mb-1 font-medium">
                  Phone Number *
                </label>
                <PhoneInput
                  international
                  defaultCountry="US"
                  value={formData.phone}
                  onChange={handlePhoneChange}
                  className="w-full border border-kenya-black rounded focus:ring-2 focus:ring-kenya-green focus:border-transparent"
                />
                {phoneError && (
                  <p className="text-red-500 text-sm mt-1">{phoneError}</p>
                )}
              </motion.div>

              <motion.div variants={itemVariants}>
                <label className="block text-kenya-black mb-1 font-medium">
                  Current Location (City/Region) *
                </label>
                <input
                  type="text"
                  name="location"
                  value={formData.location}
                  onChange={handleInputChange}
                  required
                  className="w-full p-2 border border-kenya-black rounded focus:ring-2 focus:ring-kenya-green focus:border-transparent"
                />
              </motion.div>

              <motion.div variants={itemVariants}>
                <label className="block text-kenya-black mb-1 font-medium">
                  Your Signature *
                </label>
                <div className="border border-kenya-black rounded p-2">
                  <SignatureCanvas
                    penColor='black'
                    canvasProps={{
                      width: 400,
                      height: 150,
                      className: 'w-full h-32 md:h-40 border border-gray-300 bg-white'
                    }}
                    onEnd={() => {
                      const signatureData = signatureCanvas.current?.toDataURL();
                      setFormData(prev => ({
                        ...prev,
                        signature: signatureData
                      }));
                    }}
                    ref={signatureCanvas}
                  />
                </div>
                <button 
                  type="button" 
                  onClick={() => {
                    signatureCanvas.current?.clear();
                    setFormData(prev => ({ ...prev, signature: null }));
                  }}
                  className="mt-2 text-kenya-red text-sm hover:underline"
                >
                  Clear Signature
                </button>
              </motion.div>

              <motion.div variants={itemVariants} className="flex items-start">
                <input
                  type="checkbox"
                  name="termsAgreed"
                  checked={formData.termsAgreed}
                  onChange={handleInputChange}
                  required
                  className="mt-1 mr-2"
                />
                <span className="text-kenya-black text-sm">
                  I agree to the <a href="/terms" className="text-kenya-red hover:underline" target="_blank" rel="noopener noreferrer">Terms</a>
                </span>
              </motion.div>

              <div className="my-4">
            <ReCAPTCHA
  ref={recaptchaRef}
  sitekey={process.env.REACT_APP_RECAPTCHA_SITE_KEY}
  onChange={(token) => {
    setRecaptchaToken(token);
    setRecaptchaError("");
  }}
  onExpired={() => {
    setRecaptchaToken(null);
    setRecaptchaError("reCAPTCHA expired. Please verify again.");
  }}
  onErrored={() => {
    setRecaptchaError("reCAPTCHA failed to load. Please refresh the page.");
    if (recaptchaRef.current) {
      setTimeout(() => recaptchaRef.current.reset(), 1000);
    }
  }}
  asyncScriptOnLoad={() => {
    // Called when reCAPTCHA script loads successfully
    setRecaptchaError("");
  }}
  className="flex justify-center"
/>
                {recaptchaError && (
                  <p className="text-red-500 text-sm mt-2">{recaptchaError}</p>
                )}
              </div>

              <motion.button
                variants={itemVariants}
                whileHover={{ scale: 1.03 }}
                whileTap={{ scale: 0.98 }}
                disabled={isSubmitting || !recaptchaToken || !formData.signature}
                className={`w-full py-3 rounded-lg font-bold text-lg ${
                  isSubmitting || !recaptchaToken || !formData.signature 
                    ? 'bg-gray-400 cursor-not-allowed' 
                    : 'bg-gradient-to-r from-kenya-red to-kenya-green text-white shadow-lg hover:shadow-xl transition-all'
                }`}
              >
                {isSubmitting ? (
                  <span className="flex items-center justify-center">
                    <motion.span
                      animate={{ rotate: 360 }}
                      transition={{ repeat: Infinity, duration: 1 }}
                      className="inline-block h-5 w-5 border-2 border-white border-t-transparent rounded-full mr-2"
                    />
                    Processing...
                  </span>
                ) : (
                  'Sign Petition Now'
                )}
              </motion.button>
            </form>
          </motion.div>
        </div>

        <motion.div 
          variants={itemVariants}
          className="mt-12 text-center"
        >
          <h3 className="text-xl font-semibold mb-4">Share With Others</h3>
          <div className="flex flex-wrap justify-center gap-2">
            <motion.a
              whileHover={{ y: -3 }}
              href={`https://twitter.com/intent/tweet?text=${encodeURIComponent("Join me in signing the Kenyans in Diaspora Petition!")}&url=${encodeURIComponent("http://kenyaindiasporapetition.com/")}`}
              target="_blank"
              rel="noopener noreferrer"
              className="bg-blue-400 text-white px-4 py-2 rounded-lg flex items-center"
            >
              <FiTwitter className="mr-2" /> Twitter
            </motion.a>
            <motion.a
              whileHover={{ y: -3 }}
              href={`https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent("http://kenyaindiasporapetition.com/")}`}
              target="_blank"
              rel="noopener noreferrer"
              className="bg-blue-600 text-white px-4 py-2 rounded-lg flex items-center"
            >
              <FiFacebook className="mr-2" /> Facebook
            </motion.a>
            <motion.a
              whileHover={{ y: -3 }}
              href={`https://wa.me/?text=${encodeURIComponent("Join the Kenyans in Diaspora Petition: http://kenyaindiasporapetition.com/")}`}
              target="_blank"
              rel="noopener noreferrer"
              className="bg-green-500 text-white px-4 py-2 rounded-lg flex items-center"
            >
              <FaWhatsapp className="mr-2" /> WhatsApp
            </motion.a>
            <motion.a
              whileHover={{ y: -3 }}
              href={`sms:?body=${encodeURIComponent("Join the Kenyans in Diaspora Petition: http://kenyaindiasporapetition.com/")}`}
              className="bg-purple-500 text-white px-4 py-2 rounded-lg flex items-center"
            >
              <FiMessageSquare className="mr-2" /> SMS
            </motion.a>
            <motion.button
              whileHover={{ y: -3 }}
              onClick={() => {
                const qrWindow = window.open('', 'QR Code', 'width=300,height=400');
                qrWindow.document.write(`
                  <html>
                    <head>
                      <title>QR Code for Petition</title>
                      <style>
                        body { font-family: Arial, sans-serif; text-align: center; padding: 20px; }
                        h2 { color: #333; }
                        .qr-container { margin: 20px auto; padding: 10px; background: white; display: inline-block; }
                        .url { margin-top: 20px; word-break: break-all; color: #0066cc; }
                      </style>
                    </head>
                    <body>
                      <h2>Scan to Share Petition</h2>
                      <div class="qr-container">
                        <img src="https://api.qrserver.com/v1/create-qr-code/?size=200x200&data=http://kenyaindiasporapetition.com/" alt="QR Code"/>
                      </div>
                      <div class="url">http://kenyaindiasporapetition.com/</div>
                      <p>Share this QR code with others to help grow the petition</p>
                    </body>
                  </html>
                `);
              }}
              className="bg-gray-700 text-white px-4 py-2 rounded-lg flex items-center"
            >
              <FaQrcode className="mr-2" /> QR Code
            </motion.button>
          </div>
        </motion.div>
      </div>
    </motion.div>
  );
};

export default Home;